import { useMetadataQuery } from "@/hooks/metadata";
import Checkbox from "@ingka/checkbox";
import { AssetDto, UploadWithSlimAssetsDtoStateEnum } from "generated";
import { AuthenticatedImage } from "../AuthenticatedResource/AuthenticatedImage";
import { metadataSections } from "./constants";
import { applyTransformation } from "./utils";
import Text from "@ingka/text";
import similarImagesIcon from "@ingka/ssr-icon/paths/image-stack";
import documentIcon from "@ingka/ssr-icon/paths/document";
import Button from "@ingka/button";
import Skeleton from "@ingka/skeleton";
import SSRIcon from "@ingka/ssr-icon";
import { ValidationEntry } from "@/services/store/slices/mandatoryMetaData/types";
import { isUploadFinalized } from "@/components/MetadataFields/formFieldsAccessRules";

type Props = {
  asset: AssetDto;
  isChecked: boolean;
  visibility: string[];
  errors: ValidationEntry[];
  loadingSimilarAssets: boolean;
  uploadState: UploadWithSlimAssetsDtoStateEnum;
  onChecked: () => void;
  onSimilarAssets: (assetId: number) => void;
  onOpenImageGallery: () => void;
};

const AssetBlock = ({
  asset,
  isChecked,
  visibility,
  onChecked,
  errors,
  onSimilarAssets,
  loadingSimilarAssets,
  onOpenImageGallery,
  uploadState,
}: Props) => {
  const { data: { producers, producedBy, bynder, documentContentTypes } = {} } =
    useMetadataQuery();
  const assetName = asset.name ? asset.name : asset.filename;

  const renderMediaElement = () => {
    const assetExtension = asset.extension?.toLowerCase();
    if (assetExtension === "docx" || assetExtension === "xlsx") {
      return (
        <div className="flex size-full items-center justify-center">
          <SSRIcon
            paths={documentIcon}
            aria-label="Document preview"
            className="size-8 text-neutral-500"
          />
        </div>
      );
    }
    return (
      <AuthenticatedImage
        assetId={asset.id}
        className="h-full w-full object-contain"
      />
    );
  };

  return (
    <div className="mt-space150 grid grid-cols-3">
      <div className="flex flex-col">
        <div className="flex flex-row items-center">
          <Checkbox
            id={asset.uniqueId}
            value="checkbox"
            checked={isChecked}
            className="mr-[16px]"
            aria-label={`Select ${assetName}`}
            disabled={isUploadFinalized({ uploadState })}
            onChange={onChecked}
          />
          <div className="h-space1350 w-[23rem]">
            <button
              className="flex h-full w-full items-center justify-center align-middle"
              disabled={asset.assetType === "DOCUMENT"}
              onClick={onOpenImageGallery}
            >
              {renderMediaElement()}
            </button>
          </div>
        </div>
        <div className="ml-space250 mt-space75 flex w-[23rem] items-center justify-between">
          <Text
            tagName="p"
            bodySize="m"
            className="max-w-[14rem] cursor-pointer truncate"
            title={assetName}
          >
            {assetName}
          </Text>
          {asset.assetType === "IMAGE" &&
            (loadingSimilarAssets ? (
              <Skeleton className="h-[2rem] max-w-[9.375rem]" />
            ) : (
              <Button
                type="tertiary"
                size="xsmall"
                text="Similar assets"
                iconPosition="trailing"
                ssrIcon={similarImagesIcon}
                onClick={() => onSimilarAssets(asset.id)}
              />
            ))}
        </div>
      </div>
      <div className="col-span-2 grid grid-cols-3">
        {metadataSections.map(
          ({ id, title, transformation, isConditionallyRequired }) => {
            const value = applyTransformation(
              asset[id],
              transformation,
              producers,
              producedBy,
              bynder,
              documentContentTypes,
            );

            const error = errors?.find((e) => e.field === id);
            const hidden = visibility.find((v) => v === id);
            if (hidden) {
              return null;
            }

            const fieldIsCurrentlyOptional =
              isConditionallyRequired && !isConditionallyRequired(value, asset);
            if (fieldIsCurrentlyOptional && !value) {
              return null;
            }

            const renderedValue = Array.isArray(value) ? (
              value.map((v) => (
                <Text key={v} tagName="span" bodySize="m">
                  {v}
                </Text>
              ))
            ) : (
              <Text tagName="span" bodySize="m">
                {value}
              </Text>
            );

            return (
              <div className="flex flex-col" key={id}>
                <Text tagName="h3" headingSize="xs">
                  {title}
                </Text>
                {error && (
                  <Text tagName="span" className="text-red-500" bodySize="s">
                    {errors?.find((e) => e.field === id)?.message}
                  </Text>
                )}
                {renderedValue}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default AssetBlock;
